import React, { Fragment } from "react";
import TextStyleProperties from "../app2components/panel/element/text/TextProperties";
import TextFormProperties from "../app2components/panel/element/text/TextFormProperties";
import TextAdvancedProperties from "../app2components/panel/element/text/TextAdvancedProperties";
import TextWPProperties from "../app2components/panel/element/text/TextWPProperties";
import SimpleProperties from "../app2components/panel/element/simple/SimpleProperties";
import LineProperties from "../app2components/panel/element/line/LineProperties";
import ElementPropertiesVideo from "./ElementPropertiesVideo";
import ElementPropertiesLottie from "./ElementPropertiesLottie";
import GalleryProperties from "../app2components/panel/element/gallery/GalleryProperties";
import SocialGridProperties from "../app2components/panel/element/socialGrid/SocialGridProperties";
import ElementPropertiesGalleryImages from "../app2components/panel/element/gallery/GalleryImagesProperties";
import GraphicProperties from "../app2components/panel/element/image/GraphicProperties";
import EmbedProperties from "../app2components/panel/element/embed/EmbedProperties";
import IconProperties from "../app2components/panel/element/icon/IconProperties";
import GraphicScaleProperties from "../app2components/panel/element/image/GraphicScaleProperties";
import CustomizeEffects from "../app2components/panel/shared/CustomizeEffects";
import AppConstants from "../constants/AppConstants";
import PropertyPanelSectionWrapper from "../app2components/base/PropertyPanelSectionWrapper";
import ButtonProperties from "@/app2components/panel/element/button/ButtonProperties";
import FormFieldProperties from "@/app2components/panel/element/formfield/FormFieldProperties";
import { FORMS_FLAG } from "../constants/FeatureFlags.mjs";
import {
  FORM_FIELD_ELEMENT_TYPES,
  isFormFieldElementType,
} from "../constants/FormFieldConstants.mjs";
import useV2Feature from "../hooks/useV2Feature";

const {
  ELEMENT_TYPE_BUTTON,
  ELEMENT_TYPE_FORM_CHECKBOX,
  ELEMENT_TYPE_FORM_INPUT,
  ELEMENT_TYPE_FORM_RADIO,
  ELEMENT_TYPE_FORM_SELECT,
  ELEMENT_TYPE_GALLERY,
  ELEMENT_TYPE_GRAPHIC,
  ELEMENT_TYPE_HTML_TEXT,
  ELEMENT_TYPE_ICON,
  ELEMENT_TYPE_IFRAME,
  ELEMENT_TYPE_LINE,
  ELEMENT_TYPE_SIMPLE,
  ELEMENT_TYPE_SOCIAL_GRID,
  ELEMENT_TYPE_SVG,
  ELEMENT_TYPE_TEXT,
  ELEMENT_TYPE_VIDEO,
  ELEMENT_TYPE_LOTTIE,
  WP_FIELD_POST_CONTENT,
  PANEL_SECTION_ELEMENT_BUTTON,
  PANEL_SECTION_ELEMENT_FORM_FIELD,
  PANEL_SECTION_ELEMENT_ICON,
  PANEL_SECTION_ELEMENT_SIMPLE,
  PANEL_SECTION_ELEMENT_LINE,
  PANEL_SECTION_ELEMENT_GRAPHIC,
  PANEL_SECTION_ELEMENT_SVG,
  PANEL_SECTION_ELEMENT_TEXT,
  PANEL_SECTION_ELEMENT_VIDEO,
  PANEL_SECTION_ELEMENT_LOTTIE,
  PANEL_SECTION_ELEMENT_GALLERY,
  PANEL_SECTION_ELEMENT_SOCIAL_GRID,
  PANEL_SECTION_ELEMENT_IFRAME,
  PANEL_SECTION_ELEMENT_TEXT_FORM,
  PANEL_SECTION_ELEMENT_GALLERY_IMAGES,
  PANEL_SECTION_ELEMENT_FALLBACK_IMAGES,
} = AppConstants;

/**
 * @typedef {Object} SharedProps
 * @property {string} elementId - The ID of the element.
 * @property {Immutable.Map} element - The element data.
 * @property {Immutable.Map} block - The block data.
 * @property {boolean} isLocked - Whether the element is locked.
 * @property {boolean} isWP - Whether the element is a WordPress element.
 * @property {string} layout - The layout type.
 * @property {Boolean} mixed
 * @property {Function} onPropUpdate - Callback function for property updates.
 * @property {Immutable.Map} theme - The theme data.
 */

/**
 * Wraps non styled text props into a single panel.
 * @param {SharedProps} props
 * @returns
 */
const TextProperties = (props) => {
  const wpField = props.element.getIn(["wp", "field"]);
  const isFormField = props.element.getIn(["form", "isField"], false);
  const showWP = (props.isWP && !isFormField) || wpField;

  return (
    <>
      {showWP && <TextWPProperties {...props} />}
      <TextAdvancedProperties {...props} />
      <TextFormProperties {...props} />
    </>
  );
};

/**
 * Determines if an element can have inline links.
 * @param {SharedProps} props
 * @returns {Boolean}
 */
const hasInlineLink = ({ element, isWP }) => {
  const wpField = element.getIn(["wp", "field"]);
  const isWPElement = wpField && isWP;

  return !(isWPElement && WP_FIELD_POST_CONTENT !== wpField);
};

/**
 * Returns true if icon contents should be editable.
 * @param {SharedProps} props
 * @returns {Boolean}
 */
const canEditIconContent = ({ mixed }) => !mixed;

const FORM_FIELD_TITLES = {
  [ELEMENT_TYPE_FORM_INPUT]: "Text Field",
  [ELEMENT_TYPE_FORM_SELECT]: "Dropdown Field",
  [ELEMENT_TYPE_FORM_RADIO]: "Radio Field",
  [ELEMENT_TYPE_FORM_CHECKBOX]: "Checkbox Field",
};

const formFieldTitle = ({ element }) => FORM_FIELD_TITLES[element.get("type")];

/**
 * Defines configuration for each panel section, including:
 * - title: Display name for the section
 * - key: Unique identifier
 * - components: Array of components to render in this section
 * - additionalProps: Optional dynamic props (calculated at runtime)
 * - hasWrapper: Whether to wrap in a PropertyPanelSectionWrapper
 * - excludeOnMixed: Excludes components when mixed is set to true
 *
 * @type {Record<string, {
 * title: string | ((props: SharedProps) => string),
 * key: string,
 * components: Array<React.ComponentType<SharedProps>>,
 * additionalProps?: Record<string, (sharedProps: Object) => any>,
 * hasWrapper?: Boolean,
 * excludeOnMixed?: Boolean,
 * }>}
 */
const SECTION_MAP = {
  [PANEL_SECTION_ELEMENT_BUTTON]: {
    title: "Button Style",
    key: "buttonProps",
    components: [ButtonProperties],
  },
  [PANEL_SECTION_ELEMENT_SIMPLE]: {
    title: "Shape Style",
    key: "simpleProps",
    components: [SimpleProperties],
  },
  [PANEL_SECTION_ELEMENT_LINE]: {
    title: "Line Style",
    key: "lineProps",
    components: [LineProperties],
  },
  [PANEL_SECTION_ELEMENT_GRAPHIC]: {
    title: "Image",
    key: "imageProps",
    components: [GraphicProperties, GraphicScaleProperties],
  },
  [PANEL_SECTION_ELEMENT_SVG]: {
    title: "SVG",
    key: "svgProps",
    components: [GraphicProperties, CustomizeEffects],
  },
  [PANEL_SECTION_ELEMENT_ICON]: {
    title: "Icon Settings",
    key: "iconProps",
    components: [IconProperties],
    additionalProps: {
      editContent: canEditIconContent,
    },
  },
  [PANEL_SECTION_ELEMENT_TEXT]: {
    title: "Text Style",
    key: "textFormatProps",
    components: [TextStyleProperties],
    additionalProps: {
      inlineLinksEnabled: hasInlineLink,
    },
  },
  [PANEL_SECTION_ELEMENT_TEXT_FORM]: {
    title: "Text Properties",
    key: "textPropertiesProps",
    components: [TextProperties],
  },
  [PANEL_SECTION_ELEMENT_VIDEO]: {
    title: "Video",
    key: "videoProps",
    components: [ElementPropertiesVideo],
  },
  [PANEL_SECTION_ELEMENT_LOTTIE]: {
    title: "Lottie",
    key: "lottieProps",
    components: [ElementPropertiesLottie],
  },
  [PANEL_SECTION_ELEMENT_GALLERY]: {
    title: "Gallery Settings",
    key: "galleryProps",
    components: [GalleryProperties],
  },
  [PANEL_SECTION_ELEMENT_GALLERY_IMAGES]: {
    title: "Gallery Images",
    key: "galleryImages",
    components: [ElementPropertiesGalleryImages],
    hasWrapper: false,
    excludeOnMixed: true,
  },
  [PANEL_SECTION_ELEMENT_SOCIAL_GRID]: {
    title: "Social Grid Settings",
    key: "socialGridProps",
    components: [SocialGridProperties],
  },
  [PANEL_SECTION_ELEMENT_FALLBACK_IMAGES]: {
    title: "Fallback Images",
    key: "fallbackImageProps",
    components: [ElementPropertiesGalleryImages],
    hasWrapper: false,
    excludeOnMixed: true,
  },
  [PANEL_SECTION_ELEMENT_IFRAME]: {
    title: "Embed Info",
    key: "embedProps",
    components: [EmbedProperties],
  },
  [PANEL_SECTION_ELEMENT_FORM_FIELD]: {
    title: formFieldTitle,
    key: "formFieldProps",
    components: [FormFieldProperties],
  },
};

/**
 * Maps element types to their applicable property sections
 * This determines which sections are shown for each element type
 */
const ELEMENT_PROPERTY_SECTIONS_MAP = {
  [ELEMENT_TYPE_BUTTON]: [PANEL_SECTION_ELEMENT_BUTTON],
  [ELEMENT_TYPE_SIMPLE]: [PANEL_SECTION_ELEMENT_SIMPLE],
  [ELEMENT_TYPE_LINE]: [PANEL_SECTION_ELEMENT_LINE],
  [ELEMENT_TYPE_GRAPHIC]: [PANEL_SECTION_ELEMENT_GRAPHIC],
  [ELEMENT_TYPE_SVG]: [PANEL_SECTION_ELEMENT_SVG],
  [ELEMENT_TYPE_ICON]: [PANEL_SECTION_ELEMENT_ICON],
  [ELEMENT_TYPE_TEXT]: [PANEL_SECTION_ELEMENT_TEXT, PANEL_SECTION_ELEMENT_TEXT_FORM],
  [ELEMENT_TYPE_HTML_TEXT]: [PANEL_SECTION_ELEMENT_TEXT, PANEL_SECTION_ELEMENT_TEXT_FORM],
  [ELEMENT_TYPE_VIDEO]: [PANEL_SECTION_ELEMENT_VIDEO],
  [ELEMENT_TYPE_LOTTIE]: [PANEL_SECTION_ELEMENT_LOTTIE],
  [ELEMENT_TYPE_GALLERY]: [PANEL_SECTION_ELEMENT_GALLERY_IMAGES, PANEL_SECTION_ELEMENT_GALLERY],
  [ELEMENT_TYPE_SOCIAL_GRID]: [
    PANEL_SECTION_ELEMENT_SOCIAL_GRID,
    PANEL_SECTION_ELEMENT_FALLBACK_IMAGES,
  ],
  [ELEMENT_TYPE_IFRAME]: [PANEL_SECTION_ELEMENT_IFRAME],
  ...Object.fromEntries(
    FORM_FIELD_ELEMENT_TYPES.map((type) => [type, [PANEL_SECTION_ELEMENT_FORM_FIELD]]),
  ),
};

/**
 + Workaround to support components with their own wrapper. Ideally we remove this for consistency.
 * @param {Object} props
 * @param {React.ReactNode} props.children - Child components
 * @param {Object|undefined} props.wrapperProps - Props for wrapper, if undefined no wrapper is used
 */
const Wrapper = ({ children, wrapperProps }) =>
  wrapperProps ? (
    <PropertyPanelSectionWrapper {...wrapperProps}>{children}</PropertyPanelSectionWrapper>
  ) : (
    <Fragment>{children}</Fragment>
  );

/**
 * Component to render properties specific to an element type. Usually styles.
 *
 * @param {Object} props - The properties object.
 * @param {keyof typeof SECTION_MAP} props.activeSection - The currently active section.
 * @param {import("immutable").Map} props.block - The block data.
 * @param {import("immutable").Map} props.element - The element data.
 * @param {string} props.elementId - The ID of the element.
 * @param {boolean} props.isLocked - Whether the element is locked.
 * @param {string} props.layout - The layout type.
 * @param {boolean} props.isWP - Whether the element is a WordPress element.
 * @param {Boolean} props.mixed - Property panel represents a mixed set of elements.
 * @param {Function} props.onPropUpdate - Callback function for property updates.
 * @param {(sectionName: string) => void} props.onToggle - Callback function to toggle the visibility of a section.
 * @param {import("immutable").Map} props.preparedElement - The "prepared" element.
 * @param {import("immutable").Map} props.theme - The theme data.
 * @param {import("immutable").Map} props.mappedElement - The mapped element. Not to be confused with the prepared element.
 * @returns {React.ReactElement} The rendered properties panel.
 */
const ElementTypeProperties = ({
  activeSection,
  block,
  element,
  elementId,
  isLocked,
  isWP = false,
  layout,
  mixed = false,
  onPropUpdate,
  onToggle,
  preparedElement,
  theme,
  mappedElement,
}) => {
  const elementType = element.getIn(["type"]);
  const isFormFieldsEnabled = useV2Feature(FORMS_FLAG);
  const formFieldsDisabled = isFormFieldElementType(elementType) && !isFormFieldsEnabled;
  const sections = formFieldsDisabled ? [] : (ELEMENT_PROPERTY_SECTIONS_MAP[elementType] ?? []);

  const sharedProps = {
    block,
    element,
    elementId,
    isLocked,
    isWP,
    layout,
    mixed,
    onPropUpdate,
    preparedElement,
    theme,
    mappedElement,
  };

  return sections.map((sectionName) => {
    const {
      title,
      key,
      components = [],
      additionalProps = {},
      hasWrapper = true,
      excludeOnMixed,
    } = SECTION_MAP[sectionName];

    const wrapperProps = {
      title: typeof title === "function" ? title(sharedProps) : title,
      open: activeSection === sectionName,
      onSIPanelToggle: () => onToggle(sectionName),
      onToggle: () => onToggle(sectionName),
      ...(mixed ? { className: "multiselect-panel-section" } : {}),
    };

    const preparedAdditionalProps = Object.entries(additionalProps).reduce(
      (result, [propName, preparer]) => {
        result[propName] = preparer(sharedProps);
        return result;
      },
      !hasWrapper ? wrapperProps : {},
    );

    return mixed && excludeOnMixed ? null : (
      <Wrapper key={key} wrapperProps={hasWrapper ? wrapperProps : undefined}>
        {components.map((Component, index) => (
          <Component
            {...sharedProps}
            {...preparedAdditionalProps}
            key={`${sectionName}-${index}`}
          />
        ))}
      </Wrapper>
    );
  });
};

export default ElementTypeProperties;
